/** Display primitives for the external-ID mappings grid. */
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Chip, Tooltip } from '@mui/material';

import type { ExtIdFacet, ExtIdFacetState, ExtIdKind, ExtIdModel } from '@/api/extIds';

export const mono = { fontFamily: 'monospace', fontSize: 13 } as const;

export const dash = (
  <Box component="span" sx={{ color: 'text.disabled' }}>
    —
  </Box>
);

const KIND_LABEL: Record<ExtIdKind, string> = {
  primary: 'primary',
  alias: 'alias',
};

export function KindChip({ kind }: { kind: ExtIdKind }) {
  return (
    <Tooltip
      title={
        kind === 'primary'
          ? "The entity's own custom_id. It has no companion flags, so it is always matchable."
          : 'An Additional_Ext_ID row: typed, flaggable, and able to carry tags and fan-out targets.'
      }
    >
      <Chip
        size="small"
        variant={kind === 'primary' ? 'filled' : 'outlined'}
        color={kind === 'primary' ? 'default' : 'info'}
        label={KIND_LABEL[kind]}
      />
    </Tooltip>
  );
}

export function ModelChip({ model }: { model: ExtIdModel }) {
  return <Chip size="small" variant="outlined" label={model} />;
}

/**
 * One "Used by" facet.
 *
 * The three states are visually distinct on purpose. `na` means the tenant
 * does not run that pipeline at all — 1728 production rows belong to
 * direct-XML operators with no booking restech, and rendering those as a
 * failure would make a correct catalogue look broken. Only `no` is styled as
 * a problem.
 */
const FACET_SX: Record<ExtIdFacetState, object> = {
  yes: { color: 'success.main', fontWeight: 500 },
  no: { color: 'error.main', fontWeight: 600 },
  na: { color: 'text.disabled', fontStyle: 'italic' },
};

const FACET_GLYPH: Record<ExtIdFacetState, string> = {
  yes: '✓',
  no: '✗',
  na: '–',
};

export function FacetCell({ label, facet }: { label: string; facet: ExtIdFacet | undefined }) {
  if (!facet) return dash;
  return (
    <Tooltip title={facet.detail ?? facet.reason}>
      <Box component="span" sx={{ ...FACET_SX[facet.state], mr: 1, whiteSpace: 'nowrap' }}>
        {FACET_GLYPH[facet.state]} {label}
      </Box>
    </Tooltip>
  );
}

/** A parked mapping, by whichever mechanism ops used. */
export function ParkedChip({
  marker,
  useForMatching,
}: {
  marker: string | null;
  useForMatching: boolean;
}) {
  if (marker) {
    return (
      <Tooltip title={`Parked by naming: the value carries "${marker}". The platform never rewrites it — clear the marker yourself, or use the matching flag instead.`}>
        <Chip size="small" color="warning" variant="outlined" label={`parked: ${marker}`} />
      </Tooltip>
    );
  }
  if (!useForMatching) {
    return (
      <Tooltip title="Parked by flag: use_for_matching is off, so product → tour matching skips this row.">
        <Chip size="small" color="warning" variant="filled" label="matching off" />
      </Tooltip>
    );
  }
  return null;
}

/** Who keeps an import-owned (managed) alias in step: its restech (and account), else
 *  a direct XML import — the owner the import's `<ext_ids>` scope names. */
export function managedSource(integration: string | null | undefined, account?: string | null): string {
  if (!integration) return 'XML import';
  return account ? `${integration} / ${account}` : integration;
}

/** The managed state in one sentence (the badge's tooltip). */
export function managedTooltip(integration: string | null | undefined, account?: string | null): string {
  return `Maintained by an import (${managedSource(integration, account)}). Take it over to edit.`;
}

/**
 * An import-owned alias (`managed`): the import updates it on every sync, so the
 * server refuses ops edits and deletes until it is taken over. Ownership, not a
 * problem — outlined and neutral, with the feed-controlled sync icon and a word.
 * Focusable, so the tooltip is reachable from the keyboard too.
 */
export function ManagedChip({
  integration,
  restechAccount = null,
  dense = false,
}: {
  integration: string | null;
  restechAccount?: string | null;
  /** A caption-line badge (the tour editor's identifiers table). */
  dense?: boolean;
}) {
  return (
    <Tooltip title={managedTooltip(integration, restechAccount)} describeChild>
      <Chip
        size="small"
        variant="outlined"
        icon={<SyncIcon />}
        label="Managed"
        tabIndex={0}
        data-managed-chip
        sx={[
          { flex: 'none', '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` } },
          dense && { height: 18, fontSize: 11, '& .MuiChip-icon': { fontSize: 12 }, '& .MuiChip-label': { px: 0.5 } },
        ]}
      />
    </Tooltip>
  );
}
